import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Lattes' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Lattes',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Lattes' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Lattes. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous à Port Ariane et dans les résidences récentes de Lattes ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous intervenons à Port Ariane, Maurin, Boirargues et dans tous les quartiers de Lattes. Les résidences récentes sont souvent équipées de baies coulissantes et de volets électriques que nous réparons. Délai 24-48h.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quel est votre délai d\'intervention à Lattes ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Notre délai standard est de 24 à 48h après votre appel, 7 jours sur 7. Proche de Montpellier, nous pouvons souvent intervenir encore plus rapidement. Pour une urgence vitrier, appelez le 09 80 80 22 42.',
      },
    },
  ],
};

export default function LattesPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Lattes | DFPRO"
        description="Baie coulissante, volet roulant ou vitrage en panne à Lattes ? DFPRO intervient 24-48h, 7j/7. Port Ariane, Maurin, Boirargues. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-lattes/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Lattes"
        subtitle="Port Ariane, Maurin, Boirargues : nos techniciens réparent vos baies coulissantes, volets roulants et vitrages à Lattes. Devis gratuit, intervention 24-48h, 7j/7."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Menuiserie en panne à Lattes ?"
        ctaText="Devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent dans tout Lattes et aux alentours, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Lattes concentre un habitat récent avec de nombreuses baies coulissantes et volets roulants électriques. Nous intervenons pour toute panne : roulettes usées, moteur de volet HS, vitrage à remplacer. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/lattes-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Lattes"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante dans les résidences récentes</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les résidences de Port Ariane et Maurin sont largement équipées de baies coulissantes. Après quelques années, les roulettes s'usent et la baie <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">devient dure à ouvrir</Link>. Un signe qu'il faut intervenir.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Roulettes</Link>, <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link>. Toutes marques d'alu.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant électrique : diagnostic et réparation</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Lattes compte énormément de <Link to="/volet-roulant-electrique-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants électriques</Link>. Moteur silencieux qui ne tourne plus, télécommande qui ne répond pas, <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">volet bloqué</Link> à mi-course : nous diagnostiquons rapidement.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">Changement moteur</Link>, <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link>, <Link to="/volet-roulant-ne-remonte-plus-herault/" className="text-accent-500 font-semibold hover:underline">volet qui ne remonte plus</Link>. Diagnostic gratuit.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/lattes-volet-02.jpg"
              alt="Réparation de volet roulant à Lattes"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/lattes-vitrage-03.webp"
              alt="Remplacement de vitrage à Lattes"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier : remplacement vitrage toutes dimensions</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link> à Lattes : <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">vitre cassée</Link>, <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link>, <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">vitrage embué</Link>. <Link to="/vitrier-urgence-herault/" className="text-accent-500 font-semibold hover:underline">Urgence</Link> sous 24h.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Fenêtres</Link> : <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémone</Link>, <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link>, <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battant</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Lattes sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Lattes : Port Ariane, Maurin, Boirargues, centre-ville. Communes voisines : <Link to="/depannage-menuiserie-montpellier/" className="text-accent-500 font-semibold hover:underline">Montpellier</Link>, <Link to="/depannage-menuiserie-perols/" className="text-accent-500 font-semibold hover:underline">Pérols</Link>, Saint-Jean-de-Védas. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}